import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Store, UpdateStoreRequest } from '../types/store';
import { isSafeHttpUrl } from '../utils/url';

export const StoreInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<UpdateStoreRequest>({});

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchStoreInfo();
  }, []);

  const fetchStoreInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const idToken = localStorage.getItem('idToken');
      if (!idToken || !user?.store_id) {
        throw new Error('認証情報が見つかりません');
      }

      const apiUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(`${apiUrl}/stores/${user.store_id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('店舗情報の取得に失敗しました');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setStore(data.data);
        setFormData({
          store_name: data.data.store_name,
          address: data.data.address || '',
          phone: data.data.phone || '',
          public_enabled: data.data.public_enabled,
          public_description: data.data.public_description || '',
          official_website: data.data.official_website || '',
          business_hours: data.data.business_hours || '',
          regular_holiday: data.data.regular_holiday || '',
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '店舗情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAdmin) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const idToken = localStorage.getItem('idToken');
      if (!idToken || !user?.store_id) {
        throw new Error('認証情報が見つかりません');
      }

      // 公開サイトにリンクとして表示するため http/https のみ許可（BM-008）
      const website = (formData.official_website ?? '').trim();
      if (website && !isSafeHttpUrl(website)) {
        throw new Error('公式WebサイトのURLは http:// または https:// で始まる形式で入力してください');
      }

      const apiUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(`${apiUrl}/stores/${encodeURIComponent(user.store_id)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, official_website: website }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '店舗情報の更新に失敗しました');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setStore(data.data);
        setSuccessMessage('店舗情報を更新しました');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '店舗情報の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/bottles')}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <span>←</span>
            <span>戻る</span>
          </button>
          <h1 className="text-xl font-bold text-gray-800">店舗情報</h1>
          <div className="w-16"></div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {successMessage}
          </div>
        )}

        {!isAdmin && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
            閲覧のみ可能です。編集するには管理者権限が必要です。
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          {/* 店舗名 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              店舗名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.store_name || ''}
              onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
              disabled={!isAdmin}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 住所 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              住所
            </label>
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              disabled={!isAdmin}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 電話番号 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              電話番号
            </label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              disabled={!isAdmin}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 営業時間 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              営業時間
            </label>
            <input
              type="text"
              value={formData.business_hours || ''}
              onChange={(e) => setFormData({ ...formData, business_hours: e.target.value })}
              disabled={!isAdmin}
              placeholder="例: 18:00～0:00"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 定休日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              定休日
            </label>
            <input
              type="text"
              value={formData.regular_holiday || ''}
              onChange={(e) => setFormData({ ...formData, regular_holiday: e.target.value })}
              disabled={!isAdmin}
              placeholder="例: 土・日曜／祝日"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 公式Webサイト */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              公式Webサイト
            </label>
            <input
              type="url"
              value={formData.official_website || ''}
              onChange={(e) => setFormData({ ...formData, official_website: e.target.value })}
              disabled={!isAdmin}
              placeholder="https://example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 公開サイト説明 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              公開サイト用説明
            </label>
            <textarea
              value={formData.public_description || ''}
              onChange={(e) => setFormData({ ...formData, public_description: e.target.value })}
              disabled={!isAdmin}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 公開サイト有効化 */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.public_enabled || false}
                onChange={(e) => setFormData({ ...formData, public_enabled: e.target.checked })}
                disabled={!isAdmin}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
              />
              <span className="text-sm font-medium text-gray-700">
                公開サイトを有効化
              </span>
            </label>
          </div>

          {/* 保存ボタン */}
          {isAdmin && (
            <div className="pt-4 border-t">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          )}
        </div>

        {/* メタ情報 */}
        {store && (
          <div className="mt-6 bg-gray-100 rounded-lg p-4 text-sm text-gray-600 space-y-1">
            <div>店舗ID: {store.store_id}</div>
            <div>店舗スラッグ: {store.store_slug || '未設定'}</div>
            <div>作成日時: {new Date(store.created_at).toLocaleString('ja-JP')}</div>
            {store.updated_at && (
              <div>更新日時: {new Date(store.updated_at).toLocaleString('ja-JP')}</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};
